import * as React from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { EmployeesCustomTablesListPageDocument } from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import Loading from 'modules/core/components/Loading'
import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import i18n from 'modules/core/lib/i18n'

import ResourceFieldsCollection from 'modules/custom_resources/store/collections/customFields/resourceFields'
import resources from 'modules/custom_resources/store/collections/customResources/resources'
import schemas from 'modules/custom_resources/store/collections/customResources/schemas'
import values from 'modules/custom_resources/store/collections/customResources/values'
import type Field from 'modules/custom_resources/store/models/customResources/Field'
import Schema from 'modules/custom_resources/store/models/customResources/Schema'
import { fetchData } from 'modules/employees/components/CustomResources/SchemaComponent/utils'
import List from 'modules/employees/components/CustomTables/List'
import type { CommonRouteContext } from 'modules/employees/lib/types'
import Page from 'modules/layout/components/Page'
import Header from 'modules/layout/components/Page/Header'

import { type RootRoute, RouteContext } from './context'

interface Props {
  rootRoute: RootRoute
}

export default function CustomTablesListPage({ rootRoute }: Props) {
  const { employee, headerTitle } = useOutletContext<CommonRouteContext>()

  const [loading, setLoading] = React.useState<boolean>(true)
  const [fields, setFields] = React.useState<Field[]>([])

  const { data } = useQuery(EmployeesCustomTablesListPageDocument)

  const resourceFields = () =>
    schemas.models.map(
      async (item) =>
        await new ResourceFieldsCollection([], {
          model_ref: 'custom_resources-schema',
          instance_id: String(item.attributes.id),
        }).fetch()
    )

  const sortFieldsByPosition = (fields: Field[]) => {
    Promise.all(resourceFields()).then((response) => {
      const flattedResponse = response.flat()

      const sortedFields = sortBy(fields, (field) => {
        return flattedResponse.find((rf) => rf.field_id === field.attributes.id)?.position
      })

      setFields(sortedFields)
    })
  }

  React.useEffect(() => {
    const fetchEmployeeCustomTables = async () => {
      setLoading(true)

      const data = await fetchData({
        topicNames: [Schema.TOPIC_NAMES.employees, Schema.TOPIC_NAMES.performance],
        attachableType: 'Employee',
        attachableId: employee.attributes.id,
      })
      sortFieldsByPosition(data.fields)
      setLoading(false)
    }

    fetchEmployeeCustomTables()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee])

  const canCustomizeTables = !!data?.employees._policies.manageCustomTableSettings.can

  return (
    <RouteContext.Provider value={rootRoute}>
      <Page.Body>
        <PortalWrapper container={headerTitle}>
          <Header.Title>{i18n.t('custom_tables.title')}</Header.Title>
        </PortalWrapper>

        {loading && <Loading />}
        {!loading && (
          <List
            attachableId={employee.attributes.id}
            attachableType="Employee"
            employee={employee}
            fields={fields}
            resources={resources.models as any}
            schemas={schemas.models}
            values={values.models}
            canManage={canCustomizeTables}
          />
        )}
        {!loading && <Outlet context={{ employee, fields }} />}
      </Page.Body>
    </RouteContext.Provider>
  )
}
